import type { IntlShape } from 'react-intl';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { ExtensionModule } from '@atlaskit/editor-common/extensions';

import {
	AGENT_MANAGED_EXTENSION_KEY,
	AGENT_MANAGED_EXTENSION_TYPE_DEFAULT,
	type AgentManagedExtensionAttributes,
	type AgentManagedExtensionTemplateConfig,
} from '../common/agentManagedExtensionPluginType';
import { messages } from '../common/messages';
import { generateLocalId } from '../common/utils';

import { getAgentManagedExtensionIconLoader, getTemplateQuickInsertIconLoader } from './icon';

/**
 * Build the ADF entity for a new agent-managed bodied extension.
 */
const buildBlockAdf = (
	extensionType: string,
	templateConfig?: AgentManagedExtensionTemplateConfig,
): ADFEntity => {
	const localId = generateLocalId();
	const attrs: AgentManagedExtensionAttributes = {
		extensionKey: AGENT_MANAGED_EXTENSION_KEY,
		extensionType,
		parameters: templateConfig
			? {
					macroParams: {
						templateConfig: {
							value: JSON.stringify(templateConfig),
						},
					},
				}
			: {},
		layout: 'default',
		localId,
	};
	return {
		type: 'bodiedExtension',
		attrs,
		content: [
			{
				type: 'paragraph',
			},
		],
	};
};

const templateQuickInsertModules = [
	{
		descriptionMessage: messages.insertExecutiveSummaryDescription,
		icon: 'executive-summary',
		key: 'agent-managed-block-executive-summary',
		keywords: ['agent', 'rovo', 'block', 'summary', 'executive', 'panel'],
		priority: 699,
		templateConfig: {
			goal: 'executive-summary',
			outputFormat: 'panel',
			sourceMode: 'current-page',
		},
		titleMessage: messages.insertExecutiveSummaryTitle,
	},
	{
		descriptionMessage: messages.insertMeetingRecapDescription,
		icon: 'meeting-recap',
		key: 'agent-managed-block-meeting-recap',
		keywords: ['agent', 'rovo', 'block', 'meeting', 'recap', 'notes'],
		priority: 698,
		templateConfig: {
			goal: 'meeting-recap',
			sourceMode: 'current-page',
		},
		titleMessage: messages.insertMeetingRecapTitle,
	},
	{
		descriptionMessage: messages.insertDecisionRegisterDescription,
		icon: 'decision-register',
		key: 'agent-managed-block-decision-register',
		keywords: ['agent', 'rovo', 'block', 'decision', 'register', 'table'],
		priority: 697,
		templateConfig: {
			goal: 'decision-register',
			outputFormat: 'table',
			sourceMode: 'manual',
		},
		titleMessage: messages.insertDecisionRegisterTitle,
	},
] satisfies Array<{
	descriptionMessage: typeof messages.insertDescription;
	icon: Parameters<typeof getTemplateQuickInsertIconLoader>[1];
	key: string;
	keywords: string[];
	priority: number;
	templateConfig: AgentManagedExtensionTemplateConfig;
	titleMessage: typeof messages.insertTitle;
}>;

/**
 * `quickInsert` items contributed by the agent-managed extension manifest.
 * Returns the ADF entity for a new agent-managed bodied extension; the
 * configure popup is auto-opened by the plugin's `view.update` once the
 * inserted node appears in the doc.
 */
export const getQuickInsertModules = ({
	extensionType = AGENT_MANAGED_EXTENSION_TYPE_DEFAULT,
	intl,
}: {
	extensionType?: string;
	intl: IntlShape;
}): ExtensionModule[] => {
	return [
		{
			key: 'agent-managed-block',
			title: intl.formatMessage(messages.insertTitle),
			description: intl.formatMessage(messages.insertDescription),
			keywords: ['agent', 'rovo', 'block', 'manage', 'prompt'],
			priority: 700,
			icon: getAgentManagedExtensionIconLoader('', 'small'),
			action: async (): Promise<ADFEntity> => {
				return buildBlockAdf(extensionType);
			},
		},
		...templateQuickInsertModules.map(
			({
				descriptionMessage,
				icon,
				key,
				keywords,
				priority,
				templateConfig,
				titleMessage,
			}): ExtensionModule => ({
				key,
				title: intl.formatMessage(titleMessage),
				description: intl.formatMessage(descriptionMessage),
				keywords,
				priority,
				icon: getTemplateQuickInsertIconLoader(intl.formatMessage(titleMessage), icon),
				action: async (): Promise<ADFEntity> => {
					return buildBlockAdf(extensionType, templateConfig);
				},
			}),
		),
	];
};
